/**
 * Daily AI Spend Mini-Card
 * ========================
 * Compact home-for-models card showing today/week credit burn, top workers,
 * and top models. Designed to live on the AI Model settings page (same page
 * users go to to change their model) so the cost trade-off is visible right
 * where they're making the choice. Backend: GET /api/credits/spend-summary.
 */
import React, { useEffect, useState } from 'react';
import { CircleNotch, ChartLineUp, Coins } from '@phosphor-icons/react';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AISpendCard() {
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.organization_id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let abort = false;
    const load = async () => {
      if (!orgId) return;
      try {
        const res = await fetchWithOrg(`${BACKEND_URL}/api/credits/spend-summary`, {}, orgId);
        if (!abort && res.ok) setData(await res.json());
      } catch { /* silent */ }
      if (!abort) setLoading(false);
    };
    load();
    return () => { abort = true; };
  }, [orgId]);

  if (loading || !data) return null;

  const balanceLow = data.balance < 50;

  return (
    <div className="bg-[rgba(0,229,192,0.03)] border border-[rgba(0,229,192,0.10)] rounded-xl p-5" data-testid="ai-spend-card">
      <div className="flex items-center gap-2 mb-4">
        <ChartLineUp size={16} className="text-[#00E5C0]" />
        <h3 className="text-sm font-semibold text-[#F0F4F8]">Your AI spend</h3>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-5">
        <div data-testid="spend-today">
          <div className="text-[10px] text-[#7D8896] uppercase tracking-[0.06em] mb-1">Today</div>
          <div className="text-xl font-semibold text-[#F0F4F8]">{data.today_spent.toLocaleString()}</div>
          <div className="text-[10px] text-[#7D8896]">credits</div>
        </div>
        <div data-testid="spend-week">
          <div className="text-[10px] text-[#7D8896] uppercase tracking-[0.06em] mb-1">Past 7 days</div>
          <div className="text-xl font-semibold text-[#F0F4F8]">{data.week_spent.toLocaleString()}</div>
          <div className="text-[10px] text-[#7D8896]">credits</div>
        </div>
        <div data-testid="spend-balance">
          <div className="text-[10px] text-[#7D8896] uppercase tracking-[0.06em] mb-1">Balance</div>
          <div className={`text-xl font-semibold ${balanceLow ? 'text-[#FBBF24]' : 'text-[#F0F4F8]'}`}>
            {data.balance.toLocaleString()}
          </div>
          <div className="text-[10px] text-[#7D8896] capitalize">{data.plan} plan</div>
        </div>
      </div>

      {data.top_workers && data.top_workers.length > 0 && (
        <div className="mb-3" data-testid="top-workers">
          <div className="text-[10px] text-[#7D8896] uppercase tracking-[0.06em] mb-1.5">Top workers (7d)</div>
          <div className="space-y-1">
            {data.top_workers.map((w) => (
              <div key={w.worker_id || w.worker_name} className="flex items-center justify-between text-xs">
                <span className="text-[#B0B8C4] truncate">{w.worker_name || 'Worker'}</span>
                <span className="text-[#F0F4F8] font-mono ml-2">{w.credits.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.top_models && data.top_models.length > 0 && (
        <div data-testid="top-models">
          <div className="text-[10px] text-[#7D8896] uppercase tracking-[0.06em] mb-1.5 flex items-center gap-1.5">
            <Coins size={11} /> Models used (7d)
          </div>
          <div className="space-y-1">
            {data.top_models.map((m) => (
              <div key={m.model_id} className="flex items-center justify-between text-xs">
                <span className="text-[#B0B8C4]">{m.model_id}</span>
                <span className="text-[#7D8896] ml-2 text-[10px]">{m.calls} calls · <span className="text-[#F0F4F8] font-mono">{m.credits}</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(!data.top_workers || data.top_workers.length === 0) && data.week_spent === 0 && (
        <p className="text-xs text-[#7D8896]" data-testid="spend-empty">
          No AI spend yet this week. Trigger a worker run to see it here.
        </p>
      )}
    </div>
  );
}

const _CircleNotch = CircleNotch; // eslint-disable-line no-unused-vars
